import React, { useRef, useState } from 'react';
import { onSubmitProps, imageDimensions, numberWithSpaces, UserRole } from '../../Constants/constants';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import EntityPage from '../../Layout/EntityPage';
import { IOverrideEntityActions } from '../../API/Interfaces/IEntityActions';
import { Query, QueryResult, Column } from 'material-table';
import ISearchBaseDto from '../../API/Interfaces/ISearchBaseDto';
import { IDeviceTableViewDto } from '../../API/Interfaces/IDeviceDtos';
import MTableCorporationGroupRow from '../Device/MTableCorporationGroupRow';
import GoalServiceAPI from '../../API/Services/GoalServiceAPI';
import { Tooltip, IconButton } from '@material-ui/core';
import { DeleteIcon } from '../../Layout/Components/Icons';
import { IConfirmPopup, IOpenPopup } from '../Graphics/GraphicsTable';
import Modal from '../../Layout/Components/Modal';
import GoalForm from './GoalForm';
import IGoalDetailsDto from '../../API/Interfaces/IGoalDtos';
import { DictImageType } from '../../API/Interfaces/Enums';
import NoRedirectingLink from '../../Layout/Components/NoRedirectingLink';
import ConfirmDeleteModal, { IConfirmDeleteModal } from '../../Layout/Components/ConfirmDeleteModal';
import { Helper } from '../../Utils';
import { UserContext } from '../../Context/UserContext';
import { isInRole } from '../../Utils/roleChecker';

export const Goals = () => {
	const userContext = React.useContext(UserContext);
	const hasEditRights =
		isInRole(userContext.userRoles, UserRole.SuperAdmin) ||
		isInRole(userContext.userRoles, UserRole.CorporationAdmin) ||
		isInRole(userContext.userRoles, UserRole.DealerAdmin);
	const { enqueueSnackbar } = useSnackbar();
	const { t } = useTranslation();
	const tableRef = useRef();
	const errorDialog = (message?: string) =>
		enqueueSnackbar(`${t('common.errorOccured')}${message ? ': ' : ''}${message && message}`, { variant: 'error' });
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<IConfirmPopup>();
	const [open, setOpen] = useState<IOpenPopup>({ open: false, edit: false });
	const [entity, setEntity] = useState<IGoalDetailsDto>();

	const fetchGoal = async function (id: string) {
		try {
			const response = await GoalServiceAPI.GetOne(id);
			if (response) return response;
			else {
				errorDialog();
			}
		} catch (e) {
			errorDialog(e.message);
		}
		return undefined;
	};

	const headers: Array<Column<any>> = [
		{ title: t('locations.table.corporationHeader'), sorting: true },
		{
			title: t('locations.table.corporationHeader'),
			field: 'corporationName',
			defaultGroupOrder: 0,
			defaultGroupSort: 'asc',
			grouping: true,
		},
		{
			title: t('repository.goals.goalTitle'),
			field: 'title',
			render: (rowData) => (
				<NoRedirectingLink
					onClick={async () => {
						const response = await fetchGoal(rowData.id);
						if (response) {
							setEntity({
								...response,
								targetAmount: numberWithSpaces(Number(response.targetAmount) / 100) || 0,
								currentAmount: numberWithSpaces(Number(response.currentAmount) / 100) || 0,
							});
							setOpen({ open: true, edit: true });
						}
					}}
				>
					{rowData.name}
				</NoRedirectingLink>
			),
		},
		{
			title: t('repository.goals.targetAmount') + ' (PLN)',
			field: 'targetAmount',
			render: (rowData) => numberWithSpaces(Number(rowData.targetAmount) / 100) || 0,
		},
		{
			title: t('repository.goals.collectedAmount') + ' (PLN)',
			field: 'currentAmount',
			render: (rowData) => numberWithSpaces(Number(rowData.currentAmount) / 100) || 0,
		},
		{
			title: t('repository.goals.vouchersNumber'),
			field: 'vouchersCount',
		},
		{
			title: t('repository.goals.description'),
			field: 'description',
		},
		{
			headerStyle: { textAlign: 'right' },
			sorting: false,
			render: (rowData) =>
				hasEditRights && (
					<Tooltip title={t('table.body.deleteTooltip') || ''} style={{ marginLeft: 'auto', display: 'block' }}>
						<IconButton onClick={() => setIsDeleteModalOpen({ isVisible: true, id: rowData.id })}>
							<DeleteIcon isSelected={true} />
						</IconButton>
					</Tooltip>
				),
		},
	];

	const EntityActions: IOverrideEntityActions<IDeviceTableViewDto, IDeviceTableViewDto> = {
		api: GoalServiceAPI,
		presetDataCallback: (data: any) => {
			data.currentAmount = Number(parseFloat(data.currentAmount.replace(/ /g, '').replace(',', '.')).toFixed(2)) * 100;
			data.targetAmount = Number(parseFloat(data.targetAmount.replace(/ /g, '').replace(',', '.')).toFixed(2)) * 100 || 0;
			return data;
		},
		setModalOpen: (e) => setOpen({ open: e, edit: false }),
		getTableData: async function (query: Query<IDeviceTableViewDto>) {
			const result: QueryResult<IDeviceTableViewDto> = {
				data: [],
				page: 0,
				totalCount: 0,
			};
			const request: ISearchBaseDto = {
				itemsPerPage: query.pageSize,
				pageNumber: query.page ? query.page + 1 : 1,
				searchPhrase: query.search,
				sortColumn: '',
				sortDirection: '',
			};

			if (query.orderBy) request.sortColumn = query.orderBy.field as string;

			if (query.orderDirection) request.sortDirection = query.orderDirection;

			try {
				const response = await EntityActions.api.GetPage(request);
				if (response.success) {
					result.data = response.data.items;
					result.page = response.data.currentPage - 1;
					result.totalCount = response.data.totalItemsCount;
					return result;
				} else {
					let message = t(`errorCodes.${response.errors[0].errorCode}`);
					enqueueSnackbar(`${t('common.errorOccured')}: ${message}`, { variant: 'error' });
				}
			} catch (e) {
				errorDialog(e.message);
			}
			return result;
		},
	};

	const tableProps: any = {
		columns: headers,
		options: { paging: true },
		components: {
			GroupRow: (p: any) => <MTableCorporationGroupRow {...p}></MTableCorporationGroupRow>,
		},
	};

	let entityPageProps = {
		tableRef: tableRef,
		EntityActions: EntityActions,
		headers: headers,
		pageTitle: t('menu.goals'),
		tableProps: tableProps,
		isDetails: false, //props.isInDetails,
		disableAddNew: !hasEditRights,
		//firstColumn: props.isInDetails ? 'name' : undefined,
	};

	function refreshTable() {
		tableRef.current && (tableRef.current! as any).onQueryChange();
	}

	const callbacks: onSubmitProps = {
		presetDataCallback: EntityActions.presetDataCallback,
		setModalOpen: (e) => setOpen({ open: e, edit: false }),
		api: GoalServiceAPI,
		refreshTable: refreshTable,
		setEntity: () => refreshTable(),
	};

	const handleDelete = async function () {
		try {
			Helper.setLoader(true);
			const response = await GoalServiceAPI.Delete(isDeleteModalOpen?.id!);
			if (response && response === 1) {
				Helper.setLoader(false);
				setIsDeleteModalOpen({ isVisible: false, id: undefined });
				enqueueSnackbar(t('common.dataSaved'), { variant: 'success' });
				refreshTable();
			} else {
				Helper.setLoader(false);
				enqueueSnackbar(t('common.errorOccured'), { variant: 'error' });
			}
		} catch (e) {
			Helper.setLoader(false);
			errorDialog(e.message);
		}
	};

	const deleteModalProps: IConfirmDeleteModal = {
		handleDelete: handleDelete,
		isOpen: isDeleteModalOpen?.isVisible || false,
		handleClose: () => setIsDeleteModalOpen({ isVisible: false, id: undefined }),
	};

	return (
		<>
			<EntityPage {...entityPageProps} />
			{open.open && (
				<Modal
					form={<GoalForm disabled={!hasEditRights} />}
					isOpen={open.open}
					callbacks={callbacks}
					entity={open.edit && entity}
					modalTitle={open.edit ? t('repository.goals.editGoal') : t('repository.goals.addGoal')}
					withFileUpload
					withAdditionalFileUpload
					warningMessage={t('repository.goals.popupMessage')}
					fileLabel={t('repository.goals.mainPicture')}
					additionalFileLabel={t('repository.goals.additionalPicture')}
					imageType={DictImageType.Goal}
					imageDimensions={imageDimensions.mainGoalImage}
					isReadOnly={!hasEditRights}
					onlyCancelButton={!hasEditRights}
				/>
			)}
			{isDeleteModalOpen?.isVisible && <ConfirmDeleteModal {...deleteModalProps} />}
		</>
	);
};
